import React from "react";
import { useTranslation } from "react-i18next";
import { Flag, MessageSquare, Square, Star } from "lucide-react";
import { MetricCard, MetricCardsGrid } from "../shared";
import type { ReviewsKpiDeltas } from "./reviewsKpiDeltas";
import type { ReviewsSummary } from "./types";

interface ReviewsKpiCardsProps {
  summary: ReviewsSummary;
  kpiDeltas: ReviewsKpiDeltas;
}

const KPI_CONFIG = [
  {
    titleKey: "analytics.reviews.kpi.overallRating",
    titleFallback: "Overall Rating",
    deltaFallback: "—",
    icon: <Star />,
    deltaKey: "overallRating" as const,
    getValue: (summary: ReviewsSummary) =>
      summary.overallRating != null ? summary.overallRating.toFixed(1) : "—",
  },
  {
    titleKey: "analytics.reviews.kpi.positiveNeutral",
    titleFallback: "Positive/Neutral",
    deltaFallback: "—",
    icon: <MessageSquare />,
    deltaKey: "positiveNeutral" as const,
    getValue: (summary: ReviewsSummary) =>
      summary.positiveNeutralPercent != null
        ? `${summary.positiveNeutralPercent}%`
        : "—",
  },
  {
    titleKey: "analytics.reviews.kpi.negative",
    titleFallback: "Negative",
    deltaFallback: "—",
    icon: <Square />,
    deltaKey: "negative" as const,
    getValue: (summary: ReviewsSummary) =>
      summary.negativePercent != null ? `${summary.negativePercent}%` : "—",
  },
  {
    titleKey: "analytics.reviews.kpi.reviewsCount",
    titleFallback: "Reviews Count",
    deltaFallback: "—",
    icon: <Flag />,
    deltaKey: "reviewsCount" as const,
    getValue: (summary: ReviewsSummary) => String(summary.reviewsCount),
  },
];

export const ReviewsKpiCards: React.FC<ReviewsKpiCardsProps> = ({
  summary,
  kpiDeltas,
}) => {
  const { t } = useTranslation();

  return (
    <MetricCardsGrid
      columns={4}
      className="w-full !grid-cols-2 xl:!grid-cols-4"
    >
      {KPI_CONFIG.map((metric) => {
        const delta = kpiDeltas[metric.deltaKey];

        return (
          <MetricCard
            key={metric.titleKey}
            title={t(metric.titleKey, metric.titleFallback)}
            value={metric.getValue(summary)}
            delta={
              delta.delta !== "—"
                ? t(
                    "analytics.reviews.kpi.deltaVsPrevious",
                    "{{delta}} vs previous period",
                    {
                      delta: delta.delta,
                    },
                  )
                : t(metric.deltaFallback, "—")
            }
            trend={delta.trend}
            icon={metric.icon}
            width="fill"
          />
        );
      })}
    </MetricCardsGrid>
  );
};
